import React from 'react';
import { Link } from 'react-router-dom';

import { Pencil } from 'lucide-react';

import useHasAccess from '@/features/auth/hooks/useHasAccess';
import { useAuthState } from '@/features/auth/store';
import useProviders from '@/features/provider/hooks/useProviders';
import { PAGES } from '@/shared/constants/access';
import { TooltipWrapper } from '@indi/ui';
import { cn } from '@indi/utils';
import { format, isValid, parse } from 'date-fns';

const getVisitDatePart = (visitKey: string): string => {
  const splittedKeys = visitKey.split('-');
  return splittedKeys.length > 1 ? splittedKeys[splittedKeys.length - 1]?.trim() : visitKey;
};

interface TimelineVisitHeaderProps {
  visitKey: string;
  visitProviderUuid?: string;
  patientUuid?: string;
  visitUuid?: string;
  isClosed?: boolean;
}

export const TimelineVisitHeader: React.FC<TimelineVisitHeaderProps> = ({
  visitKey,
  visitProviderUuid,
  patientUuid,
  visitUuid,
  isClosed,
}) => {
  const { findProviderById } = useProviders();
  const providerName = visitProviderUuid ? findProviderById(visitProviderUuid)?.display : undefined;
  const datePart = getVisitDatePart(visitKey);
  const parsed = parse(datePart, 'MM/dd/yyyy hh:mm a', new Date());
  const hasValidDate = isValid(parsed);
  const { providerUuid: authProviderUuid, isSuperAdmin } = useAuthState();
  const hasAccessToRx = useHasAccess(PAGES.PRESCRIPTION);

  // Only the visit's author (visit provider) may add missed notes to it.
  const canAmend =
    hasAccessToRx && (isSuperAdmin || (Boolean(visitProviderUuid) && visitProviderUuid === authProviderUuid));

  const amendLink =
    isClosed && canAmend && patientUuid && visitUuid ? (
      <TooltipWrapper content="Add missed notes">
        <Link
          to={`/patient/${patientUuid}/prescription?visit=${visitUuid}`}
          onClick={e => e.stopPropagation()}
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            'border border-primary/20 bg-primary/10 text-primary',
            'hover:bg-primary/15',
          )}
          aria-label="Add missed notes"
        >
          <Pencil size={14} />
        </Link>
      </TooltipWrapper>
    ) : null;
  return (
    <div className="relative min-w-[120px] px-2 py-1 pr-10 text-center">
      {hasValidDate ? (
        <>
          <div className="text-sm font-semibold text-slate-800">{format(parsed, 'MMM dd, yyyy')}</div>
          <div className="text-xs font-medium text-slate-500">{format(parsed, 'h:mm a')}</div>
        </>
      ) : (
        <span className="text-sm font-semibold text-slate-800">{visitKey}</span>
      )}
      {providerName ? <div className="mt-0.5 text-xs font-medium text-slate-500">{providerName}</div> : null}
      {amendLink ? <div className="absolute right-1 top-1">{amendLink}</div> : null}
    </div>
  );
};
